import { SignIn as ClerkSignIn, useClerk } from '@clerk/react';
import { Anchor, Center, Loader, useMantineTheme } from '@mantine/core';
import { Button } from '@vrplatform/components';
import {
  getOwnerPortalInvitationUrl,
  getOwnerPortalRedirectUrl,
  useOwnerPortal,
} from '@vrplatform/data';
import { type ComponentProps, useEffect } from 'react';
import { useLocation } from 'react-router';
import { EmptyViewWrapper } from '../../components';

/**
 * Branded sign-in for a customer owner portal. On the satellite hostname it
 * sends owners straight to the primary domain, except for invitations, which
 * first explain the invitation; on the primary domain it renders Clerk with the
 * registration's branding and returns only to the registered HTTPS origin.
 * Owners are always invited by email, so the portal offers no sign-up: the
 * registered sign-up URL shows the same sign-in.
 */
export const OwnerPortalSignIn = () => {
  const portal = useOwnerPortal();
  const clerk = useClerk();
  const location = useLocation();
  const theme = useMantineTheme();
  if (!portal) throw new Error('Owner portal branding is required');
  const isSatellite = window.location.hostname === portal.hostname;
  let returnUrl: string;
  let invitationUrl: string | undefined;
  try {
    returnUrl = getOwnerPortalRedirectUrl(location, portal.hostname);
    if (isSatellite && location.pathname === '/invitation') {
      invitationUrl = getOwnerPortalInvitationUrl(
        window.location.href,
        clerk.buildSignInUrl({ redirectUrl: `https://${portal.hostname}/` }),
        portal
      );
    }
  } catch {
    return (
      <EmptyViewWrapper
        actions={
          <Button
            variant="primary"
            component="a"
            href={`https://${portal.hostname}/`}
          >
            Open owner portal
          </Button>
        }
        title="Invalid sign-in link"
        description="This sign-in link is incomplete or points to another site. Open your owner portal and sign in again."
      />
    );
  }
  if (isSatellite)
    return invitationUrl ? (
      <OwnerPortalInvitationLanding
        portalName={portal.name}
        href={invitationUrl}
        showPlatformBranding={portal.showPlatformBranding}
      />
    ) : (
      <SatelliteSignInRedirect
        href={clerk.buildSignInUrl({ redirectUrl: window.location.href })}
      />
    );

  // Clerk renders like the empty-state content around it: its own card chrome
  // and logo are removed and its tokens follow the design system.
  const appearance = {
    options: { logoPlacement: 'none' },
    variables: {
      colorPrimary: portal.colorPrimary,
      colorForeground: theme.colors.neutral[9],
      colorMutedForeground: theme.colors.neutral[6],
      colorBackground: theme.white,
      colorInput: theme.white,
      colorInputForeground: theme.colors.neutral[9],
      colorDanger: theme.colors.red[7],
      fontFamily: theme.fontFamily,
      fontFamilyButtons: theme.fontFamily,
      fontSize: '0.875rem',
      borderRadius: theme.radius.md,
    },
    elements: {
      rootBox: { width: '100%' },
      cardBox: {
        width: '100%',
        maxWidth: 'none',
        border: 'none',
        borderRadius: 0,
        boxShadow: 'none',
        overflow: 'visible',
      },
      // Clerk offsets the card by -1px to cover the removed card border.
      card: {
        margin: 0,
        padding: 0,
        border: 'none',
        boxShadow: 'none',
        background: 'transparent',
      },
      headerTitle: {
        fontSize: '1.7rem',
        fontWeight: Number(theme.headings.fontWeight),
      },
      // The footer only holds the sign-up link and Clerk's own branding.
      footer: { display: 'none' },
      // Owners sign in with the email address they were invited with.
      socialButtonsRoot: { display: 'none' },
      dividerRow: { display: 'none' },
      formButtonPrimary: { fontWeight: 500 },
    },
  } satisfies ComponentProps<typeof ClerkSignIn>['appearance'];

  return (
    <>
      <EmptyViewWrapper>
        <ClerkSignIn
          routing="hash"
          withSignUp={false}
          forceRedirectUrl={returnUrl}
          appearance={appearance}
        />
      </EmptyViewWrapper>
      {portal.showPlatformBranding && <PlatformBacklink />}
    </>
  );
};

/** Replaces the history entry so Back does not return to this redirect. */
const SatelliteSignInRedirect = ({ href }: { href: string }) => {
  useEffect(() => window.location.replace(href), [href]);
  return (
    <EmptyViewWrapper>
      <Loader type="dots" size="md" />
    </EmptyViewWrapper>
  );
};

/**
 * First screen of an invitation on the customer domain, before the
 * primary-domain hand-off.
 */
export const OwnerPortalInvitationLanding = ({
  portalName,
  href,
  showPlatformBranding,
}: {
  portalName: string;
  href: string;
  showPlatformBranding: boolean;
}) => (
  <>
    <EmptyViewWrapper
      title={`You have been invited to join ${portalName}`}
      description="Set up access to your owner portal. Continue with the email address this invitation was sent to."
      actions={
        <Button variant="primary" component="a" href={href}>
          Accept invitation
        </Button>
      }
    />
    {showPlatformBranding && <PlatformBacklink />}
  </>
);

/**
 * Followed backlink on the owner portal's public pages unless support turned
 * platform branding off for the portal.
 */
const PlatformBacklink = () => (
  <Center component="footer" mih="10vh">
    <Anchor href="https://www.vrplatform.app/" size="xs" c="neutral">
      Powered by VRPlatform
    </Anchor>
  </Center>
);

/**
 * Clerk names the application in several sign-in steps. On a white-labelled
 * portal those strings name the portal instead.
 */
export const getOwnerPortalClerkLocalization = (portalName: string) => ({
  signIn: {
    start: {
      title: `Sign in to ${portalName}`,
      titleCombined: `Continue to ${portalName}`,
    },
    emailCode: { subtitle: `to continue to ${portalName}` },
    emailLink: { subtitle: `to continue to ${portalName}` },
    phoneCode: { subtitle: `to continue to ${portalName}` },
    totpMfa: {
      title: 'Two-step verification',
      subtitle: 'Enter the 6-digit code from your authenticator app.',
      formTitle: 'Authentication code',
    },
    backupCodeMfa: {
      title: 'Use a backup code',
      subtitle: `Enter one of the codes you saved when you turned on two-step verification. If you lost your device and your codes, contact ${portalName}.`,
    },
  },
});
